import {
  CollapsibleTree,
  SetChildrenTreeItemParams,
  TreeNodeData,
} from './CollapsibleTree'
import {
  ProjectFragment,
  StorageItemFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

// State handling inspired by https://www.w3.org/WAI/ARIA/apg/patterns/treeview/examples/treeview-1b/

export interface TreeStateDefaults {
  selectedId: string | null
  focusedId: string | null
}

export interface CurrentIdsInput {
  /**
   * Id of the workspace where the Storage Item currently belongs to.
   */
  readonly workspaceId: string
  /**
   * Id of the project where the Storage Item currently belongs to (if it belongs to a project).
   */
  readonly projectId: string | null

  /**
   * If the storage item is a project itself, this is the id of the project.
   */
  readonly selfProjectId: string | null

  /**
   * List of storage items that should be disabled in the tree (e.g. cannot be selected as destination).
   */
  readonly disabledItems: string[]
}

export interface CurrentIds extends CurrentIdsInput {
  /**
   * Id of a direct parent (either parent project or workspace) where the Storage Item currently belongs to.
   */
  readonly currentId: string
}

export interface CurrentItemState {
  /** Parent workspace */
  workspace: WorkspaceMinimalFragment
  /** Parent project */
  project: ProjectFragment | null
  item: Pick<StorageItemFragment, '__typename' | 'identifier'>
  originType: 'stashed' | 'standard'
  disabledItems?: string[]
}

export type UpdateReason =
  | 'selected'
  | 'focused'
  | 'open'
  | 'setChildren'
  | 'creatingProject'

export interface CreatingProjectState {
  id: string
  loading: boolean
  name?: string
}

export interface TreeSearch {
  name: string | null
}

export type CreatingProjectCurrentState = 'pending' | 'loading' | null

export class TreeState<Payload extends {}> {
  /**
   * Id of a node that is currently picked.
   * e.g. project where currently the document belongs to.
   *
   * It is not expected that this id changes frequently (or at all) during the lifetime of the component.
   */
  private readonly _currentDestinationId: string | null = null
  get currentDestinationId(): string | null {
    return this._currentDestinationId
  }

  get currentDestination(): TreeNodeData<Payload> | null {
    if (!this._currentDestinationId) return null
    return this._tree.getNode(this._currentDestinationId) || null
  }

  /**
   * Id of a node that is currently selected (but not necessarily focused).
   */
  private _selectedId: string | null = null
  get selectedId(): string | null {
    return this._selectedId
  }

  get selected(): TreeNodeData<Payload> | null {
    if (!this._selectedId) return null
    return this._tree.getNode(this._selectedId) || null
  }

  /**
   * Id of a node that is currently focused (but not necessarily selected).
   */
  private _focusedId: string | null = null
  get focusedId(): string | null {
    return this._focusedId
  }

  get focused(): TreeNodeData<Payload> | null {
    if (!this._focusedId) return null
    return this._tree.getNode(this._focusedId) || null
  }

  private _creatingProjectState: CreatingProjectState | null = null
  get creatingProjectState(): CreatingProjectState | null {
    return this._creatingProjectState
  }

  private listeners: Map<string, Set<(reason: UpdateReason) => void>> =
    new Map()
  private globalListeners: Set<(reason: UpdateReason) => void> = new Set()
  private nodeRefs: Map<string, HTMLLIElement> = new Map()

  private _tree: CollapsibleTree<Payload>
  public get tree(): ReadonlyByPrefix<CollapsibleTree<Payload>> {
    return this._tree
  }

  readonly currentIds: CurrentIds

  constructor(
    readonly current: Readonly<CurrentItemState>,
    defaults: TreeStateDefaults
  ) {
    this.currentIds = {
      workspaceId: current.workspace.identifier,
      projectId: current.project?.identifier || null,
      selfProjectId:
        current.item.__typename === 'Project' ? current.item.identifier : null,
      currentId: current.project?.identifier || current.workspace.identifier,
      disabledItems: current.disabledItems || [],
    }

    this._currentDestinationId = this.currentIds.currentId

    this._selectedId = defaults.selectedId
    this._focusedId = defaults.focusedId

    this._tree = new CollapsibleTree<Payload>()
  }

  focusNode(nodeId: string): void {
    if (!nodeId) return
    const ref = this.nodeRefs.get(nodeId)

    if (ref) ref.focus()

    const previouslyFocusedId = this._focusedId

    this._focusedId = nodeId
    this.notifyListeners('focused', previouslyFocusedId, nodeId)
  }

  isCurrentDestination(id: string): boolean {
    // In reality, stashed items (Trashed or Archived) still have parent, e.g. directly workspace or my drafts project.
    // However, for the user, it acts like the parent would be a Trash or Archive lists. However, right now, those lists are not represented in the tree.
    // Thus, if the originType is 'stashed', we simply treat as there are no parents in the whole tree.
    if (this.current.originType === 'stashed') return false
    return this._currentDestinationId === id
  }

  isOpen(id: string): boolean {
    return this._tree.isOpen(id)
  }

  isSelected(id: string): boolean {
    return this._selectedId === id
  }

  isFocused(id: string): boolean {
    return this._focusedId === id
  }

  isDisabled(id: string): boolean {
    return this.currentIds.disabledItems.includes(id)
  }

  getCreatingProjectState(id: string): CreatingProjectCurrentState {
    if (this._creatingProjectState?.id !== id) return null
    return this._creatingProjectState.loading ? 'loading' : 'pending'
  }

  clearCreatingProjectState(causedById: string | null): void {
    if (!this._creatingProjectState) return

    this.onCreatingProject(causedById || '', null)
  }

  onSelect(id: string | null): void {
    if (this.currentIds.selfProjectId) {
      // Prevent selecting a node that is the same as the self project
      if (id === this.currentIds.selfProjectId) return

      // Prevent selecting a node that a child of the self project
      const parentsPath = id ? this._tree.getParentsPathToNode(id) : []
      if (parentsPath.includes(this.currentIds.selfProjectId)) return
    }

    this.clearCreatingProjectState(id)

    const previouslySelectedId = this._selectedId
    const previouslyFocusedId = this._focusedId

    this._selectedId = id
    this._focusedId = id

    this.notifyListeners(
      'selected',
      previouslySelectedId,
      previouslyFocusedId,
      id
    )
  }

  onFocusedChange(id: string | null, isFocused: boolean): void {
    const previouslyFocusedId = this._focusedId

    if (!id) this._focusedId = null

    if (isFocused) {
      this._focusedId = id
    } else if (this._focusedId === id) {
      this._focusedId = null
    }

    this.notifyListeners('focused', previouslyFocusedId, id)
  }

  onOpenChange(id: string, isOpen: boolean): void {
    this.clearCreatingProjectState(id)

    this._tree.setIsOpen(id, isOpen)
    this.notifyListeners('open', id)
  }

  onCreatingProject(id: string, state: CreatingProjectCurrentState): void {
    const previousId = this._creatingProjectState?.id || null

    try {
      if (!state) {
        this._creatingProjectState = null
        return
      }

      this._creatingProjectState = { id, loading: state === 'loading' }
    } finally {
      this.notifyListeners('creatingProject', id, previousId)
    }
  }

  openAllToNode(id: string): void {
    this.clearCreatingProjectState(id)

    const parentsPath = this._tree.getParentsPathToNode(id)
    parentsPath.forEach(id => this._tree.setIsOpen(id, true))
    this._tree.setIsOpen(id, true)

    this.notifyListeners('open', ...parentsPath, id)
  }

  private alreadyOpenedOnce: Set<string> = new Set()
  /**
   * We want to intialize the tree with all nodes opened to the node that is currently selected.
   * However, we want to do it only once, for the first time the tree is loaded.
   *
   * If user opens/closes nodes later on AND we unmound/remount the project picker (without dropping the state),
   * We don't want to reinitialize the tree to the selected node.
   */
  setOpenToOnce(id: string): void {
    if (this.alreadyOpenedOnce.has(id)) return
    this.alreadyOpenedOnce.add(id)
    this.openAllToNode(id)
  }

  setChildren(id: string, children: TreeNodeData<Payload>[]): void {
    this._tree.setChildren(id, children)
    this.notifyListeners('setChildren', id)
  }

  setChildrenTree<T extends Payload>(args: {
    list: T[]
    itemParams: (item: T) => SetChildrenTreeItemParams
  }): void {
    const { list, itemParams } = args
    const itemParamsMap = new Map(list.map(x => [x, itemParams(x)]))

    const existingParentIds = args.list
      .map(x => itemParamsMap.get(x)?.parentId || CollapsibleTree.ROOT_ID)
      .filter(x => x && this._tree.getNode(x))

    this._tree.setChildrenTree({ list, itemParamsMap })

    this.notifyListeners('setChildren', ...existingParentIds)
  }

  onRefLoaded(id: string, ref: HTMLLIElement): void {
    this.nodeRefs.set(id, ref)
  }

  registerUpdateListener(
    id: string,
    listener: (reason: UpdateReason) => void
  ): () => void {
    if (!this.listeners.has(id)) {
      this.listeners.set(id, new Set())
    }

    const listenersSet = this.listeners.get(id)!
    listenersSet.add(listener)

    return () => {
      listenersSet.delete(listener)
    }
  }

  registerUpdatesListener(
    listener: (reason: UpdateReason) => void
  ): () => void {
    this.globalListeners.add(listener)
    return () => {
      this.globalListeners.delete(listener)
    }
  }

  private notifyListeners(
    reason: UpdateReason,
    ...ids: (string | null)[]
  ): void {
    const uniqueIds = new Set(ids)
    for (const id of uniqueIds) {
      if (!id) continue
      const listeners = this.listeners.get(id)
      if (!listeners) continue

      for (const listener of listeners) {
        listener(reason)
      }
    }

    this.notifyGlobalListeners(reason)
  }

  private notifyGlobalListeners(reason: UpdateReason): void {
    for (const listener of this.globalListeners) {
      listener(reason)
    }
  }
}
